import {useCallback} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import {LOCALE_LABEL_KEYS, SUPPORTED_LOCALES} from 'GlobalShared/locales';
import {useAuth} from 'UI/hooks/useAuth';

interface LanguageOption {
    /** Whether this option is currently active. */
    isActive: boolean;
    /** Display label for the language. */
    label: string;
    /** Locale code (e.g. `'de'`, `'en'`). */
    locale: string;
}

/**
 * Custom hook for the `LanguageSwitch` component.
 * Manages the current locale state and provides handlers for switching languages
 * via the Next.js router and i18next.
 *
 * @returns An object containing:
 * - `currentLocale`: The currently active locale string.
 * - `handleLanguageChange`: Callback to switch to a given locale.
 * - `options`: Array of available language options with active state.
 * - `t`: Translation function from react-i18next.
 *
 * @example
 * ```tsx
 * const {currentLocale, handleLanguageChange, options, t} = useLanguageSwitch();
 * ```
 */
export const useLanguageSwitch = () => {
    const router = useRouter();
    const {i18n, t} = useTranslation();
    const {user} = useAuth();
    const currentLocale = router.locale ?? i18n.language;

    /**
     * Switches the app locale by navigating to the same page with a different locale.
     * Persists the choice to the user's profile when authenticated.
     *
     * @param locale The locale code to switch to.
     */
    const handleLanguageChange = useCallback(
        (locale: string) => {
            void router.push(router.asPath, router.asPath, {locale});

            if (user) {
                void fetch(`/api/users/${user.id}`, {
                    body: JSON.stringify({locale}),
                    credentials: 'include',
                    headers: {'Content-Type': 'application/json'},
                    method: 'PATCH'
                });
            }
        },
        [router, user]
    );

    const options: LanguageOption[] = SUPPORTED_LOCALES.map(locale => ({
        isActive: currentLocale === locale,
        label: t(LOCALE_LABEL_KEYS[locale]),
        locale
    }));

    return {
        currentLocale,
        handleLanguageChange,
        options,
        t
    };
};